import { useSelector } from 'react-redux';
import { RootState } from '../Store/store';
import { PageRenderProps } from './PageRegistryCore';
import { GamePageRegistry } from './GamePageRegistry';
import { CommonPageId } from './PageTypes';
import { PageMore } from '../Pages/PageMore/PageMore';
import { PageDailyStreak } from '../Pages/PageDailyStreak/PageDailyStreak';
import { PageTerms } from '../Pages/PageTerms/PageTerms';
import { PageHelp } from '../Pages/PageHelp/PageHelp';
import { PageLogin } from '../Pages/PageLogin/PageLogin';
import { PageAuthError } from '../Pages/PageAuthError/PageAuthError';
import { PageMagicLinkCallback } from '../Pages/PageMagicLinkCallback/PageMagicLinkCallback';
import { PageCloseTab } from '../Pages/PageCloseTab/PageCloseTab';
import { PageNotificationPermission } from '../Pages/PageNotificationPermission/PageNotificationPermission';
import ScreenTitle from '../ScreenTitle/ScreenTitle';
import { ButtonStylized, ButtonStylizedType } from '../ButtonStylized/ButtonStylized';
import * as Lucide from 'lucide-react';

/**
 * Common pages registry that provides shared page renderers.
 * Implements the common GamePageRegistry interface.
 */
export const commonPagesRegistry: GamePageRegistry = {
  getPageRenderers: () => ({
    [CommonPageId.Help]: ({ onRequestClose }: PageRenderProps) => {
      return <PageHelp onRequestClose={onRequestClose} />;
    },
      
    [CommonPageId.More]: ({ onRequestClose, navigateToPage }: PageRenderProps) => {
      return (
        <PageMore 
          onRequestClose={onRequestClose}
          navigateToPage={navigateToPage}
        />
      );
    },

    // TODO: consider re-adding puzzle archive here once it has been genericized to apply across games.
    // [CommonPageId.Archive]: ({ onRequestClose, navigateToPage }: PageRenderProps) => {
    //      foo();
    // }

    [CommonPageId.DailyStreak]: ({ onRequestClose }: PageRenderProps) => {
      const { player } = useSelector((state: RootState) => state.gameSession);
      
      if (!player) {
        return (
          <div style={{ padding: '20px', textAlign: 'center' }}>
            <ScreenTitle title="Daily Streak" showBackButton={false} />
            <p>Player information required to show streak data.</p>
          </div>
        );
      }
      
      return (
        <PageDailyStreak 
          onRequestClose={onRequestClose}
          playerId={player.playerId}
          currentStreak={1} // Default streak - games should provide actual streak data
          autoShow={true}
        />
      );
    },

    [CommonPageId.Terms]: ({ onRequestClose }: PageRenderProps) => {
      return <PageTerms onRequestClose={onRequestClose} />;
    },

    [CommonPageId.Login]: ({ onRequestClose, navigationProps }: PageRenderProps) => {
      const { currentGame } = useSelector((state: RootState) => state.gameSession);
      
      if (!currentGame) {
        return (
          <div style={{ padding: '20px', textAlign: 'center' }}>
            <p>Game information required for login.</p>
          </div>
        );
      }
      
      return (
        <PageLogin
          gameId={currentGame.metadata.gameId}
          onRequestClose={onRequestClose}
          message={navigationProps?.message}
          subMessage={navigationProps?.subMessage}
        />
      );
    },

    [CommonPageId.AuthError]: ({ onRequestClose, navigationProps, navigateToPage }: PageRenderProps) => {
      return (
        <PageAuthError
          errorType={navigationProps?.errorType || 'invalid'}
          onRequestNewLink={() => {
            // Close the error modal first
            if (onRequestClose) {
              onRequestClose();
            }
            // Then navigate to login page
            if (navigateToPage) {
              navigateToPage(CommonPageId.Login, {
                title: "Request New Login Link",
                subtitle: "Enter your email to receive a fresh login link."
              });
            }
          }}
        />
      );
    },

    [CommonPageId.MagicLinkCallback]: ({ onRequestClose, navigateToPage }: PageRenderProps) => {
      return (
        <PageMagicLinkCallback 
          onLoginSuccess={onRequestClose}
          onRequestClose={onRequestClose}
          navigateToPage={navigateToPage}
        />
      );
    },

    [CommonPageId.CloseTab]: ({ onRequestClose }: PageRenderProps) => {
      return <PageCloseTab onRequestClose={onRequestClose} />;
    },

    [CommonPageId.NotificationPermission]: ({ onRequestClose, navigationProps }: PageRenderProps) => {
      return (
        <PageNotificationPermission 
          message={navigationProps?.message}
          onRequestClose={onRequestClose}
          onPermissionResponse={navigationProps?.onPermissionResponse}
        />
      );
    }
  })
};